import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from "react";
import { useHistory, useLocation } from "react-router";

import useListConversations from "@/ai-chat/hooks/useListConversations";
import { invalidateQuery } from "@/api";
import { useFeatureAccess } from "@/hooks/useFeatureAccess";

import {
  activityReducer,
  type FailedEntry,
  initialActivityState,
  type ReadyEntry,
} from "./activityState";
import { showResponseReadyToast } from "./ResponseReadyToast";

const RUNNING_POLL_MS = 3000;

export function isAiChatPath(pathname: string): boolean {
  return pathname === "/ai-chat" || pathname.startsWith("/ai-chat/");
}

export type AiChatActivity = {
  runningIds: ReadonlySet<string>;
  ready: ReadyEntry[];
  failed: FailedEntry[];
  readyDismissed: boolean;
  dismissReady: () => void;
  markStopped: (id: string) => void;
  markSeen: (id: string) => void;
  markTurnStarted: (id: string) => void;
  setActiveConversation: (id: string | null) => void;
};

const noop = () => {};

const AiChatActivityContext = createContext<AiChatActivity>({
  runningIds: new Set(),
  ready: [],
  failed: [],
  readyDismissed: false,
  dismissReady: noop,
  markStopped: noop,
  markSeen: noop,
  markTurnStarted: noop,
  setActiveConversation: noop,
});

export function AiChatActivityProvider({
  children,
}: PropsWithChildren<unknown>) {
  const access = useFeatureAccess("ai_chat");
  const [state, dispatch] = useReducer(activityReducer, initialActivityState);
  const { pathname } = useLocation();
  const history = useHistory();

  const { data, dataUpdatedAt } = useListConversations({
    enabled: access.state === "feature",
    refetchInterval: state.runningIds.size > 0 ? RUNNING_POLL_MS : false,
  });

  // Keyed on dataUpdatedAt, not just data: a crashed turn returns identical
  // rows every poll, and only re-running the reducer lets its lock age out.
  useEffect(() => {
    if (data) {
      dispatch({ type: "list", conversations: data, now: Date.now() });
    }
  }, [data, dataUpdatedAt]);

  // Toast each response once, as it finishes. The ref stays on the newest
  // entry already handled, so reopening a chat (which drops its entry) or a
  // later poll never toasts the same answer again.
  const newestReady = state.ready[0];
  const toastedRef = useRef<ReadyEntry | undefined>(undefined);
  const onAiChatPage = isAiChatPath(pathname);
  useEffect(() => {
    if (!newestReady || newestReady === toastedRef.current) return;
    const isNew =
      !toastedRef.current ||
      newestReady.finishedAt > toastedRef.current.finishedAt;
    toastedRef.current = newestReady;
    if (isNew && !onAiChatPage) {
      showResponseReadyToast(newestReady, (id) =>
        history.push(`/ai-chat/${id}`),
      );
    }
  }, [newestReady, onAiChatPage, history]);

  const dismissReady = useCallback(() => dispatch({ type: "dismiss" }), []);
  const markStopped = useCallback(
    (id: string) => dispatch({ type: "stop", id, now: Date.now() }),
    [],
  );
  const markSeen = useCallback(
    (id: string) => dispatch({ type: "seen", id, now: Date.now() }),
    [],
  );
  const markTurnStarted = useCallback((id: string) => {
    dispatch({ type: "started", id });
    void invalidateQuery<"ai_chat_list_conversations">([
      "ai_chat_list_conversations",
    ]);
  }, []);
  const setActiveConversation = useCallback(
    (id: string | null) => dispatch({ type: "active", id }),
    [],
  );

  const value = useMemo<AiChatActivity>(
    () => ({
      runningIds: state.runningIds,
      ready: state.ready,
      failed: state.failed,
      readyDismissed: state.readyDismissed,
      dismissReady,
      markStopped,
      markSeen,
      markTurnStarted,
      setActiveConversation,
    }),
    [
      state.runningIds,
      state.ready,
      state.failed,
      state.readyDismissed,
      dismissReady,
      markStopped,
      markSeen,
      markTurnStarted,
      setActiveConversation,
    ],
  );

  return (
    <AiChatActivityContext.Provider value={value}>
      {children}
    </AiChatActivityContext.Provider>
  );
}

export function useAiChatActivity(): AiChatActivity {
  return useContext(AiChatActivityContext);
}
